import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTasks, updateTask, createTask, getClients, getProfiles, fullNameLastFirst } from '../lib/supabase';
import { notifyTaskAssigned } from '../lib/notifications';
import { useAuth } from '../hooks/useAuth';
import { format, differenceInDays } from 'date-fns';
import { parseLocalDate } from '../lib/dateUtils';
import DataTable from '../components/DataTable';

const STATUSES = ['Open', 'In Progress', 'Done', 'All'];
const PRIORITIES = ['Urgent', 'Normal', 'Low'];

const duePill = (dateStr, status) => {
  if (!dateStr || status === 'Done') return null;
  const days = differenceInDays(parseLocalDate(dateStr), new Date());
  if (days < 0)  return { label:`${Math.abs(days)}d overdue`, bg:'var(--danger-bg)', color:'var(--danger)' };
  if (days === 0) return { label:'Today',                      bg:'var(--danger-bg)', color:'var(--danger)' };
  if (days <= 3)  return { label:`${days}d`,                   bg:'var(--warn-bg)', color:'var(--warn)' };
  return            { label: format(parseLocalDate(dateStr),'MMM d'), bg:'var(--accent-bg)', color:'var(--accent2)' };
};

export default function Tasks() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [filter, setFilter] = useState('Open');
  const [sortBy, setSortBy] = useState('due_date');
  const [sortAsc, setSortAsc] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [form, setForm] = useState({ title:'', client_id:'', assigned_to_id:'', priority:'Normal', due_date:'', notes:'' });
  const [modalError, setModalError] = useState('');

  const load = () => {
    getTasks().then(({ data }) => setTasks(data || []));
    getClients().then(({ data }) => setClients(data || []));
    getProfiles().then(({ data }) => setStaff(data || []));
  };

  useEffect(() => { load(); }, []);

  const handleStatusChange = async (task, newStatus) => {
    await updateTask(task.id, {
      status: newStatus,
      completed_at: newStatus === 'Done' ? new Date().toISOString() : null
    });
    setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: newStatus } : t));
    // Task completions are reported in the daily digest ("Activities Completed
    // Yesterday"), not as real-time emails. completed_at (set above) is the source.
  };

  const openCreate = () => {
    setEditingTask(null);
    setModalError('');
    setForm({ title:'', client_id:'', assigned_to_id:'', priority:'Normal', due_date:'', notes:'' });
    setShowModal(true);
  };

  const openEdit = (task) => {
    setEditingTask(task);
    setModalError('');
    setForm({
      title: task.title || '',
      client_id: task.client_id || '',
      assigned_to_id: task.assigned_to_id || '',
      priority: task.priority || 'Normal',
      due_date: task.due_date || '',
      notes: task.notes || '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.title) return;
    const client = clients.find(c => c.id === form.client_id);
    const assignee = staff.find(s => s.id === form.assigned_to_id);

    if (editingTask) {
      const { data } = await updateTask(editingTask.id, {
        title: form.title,
        client_id: form.client_id || null,
        client_name: client ? `${client.first_name} ${client.last_name}` : null,
        client_ref: client?.client_id,
        assigned_to_id: form.assigned_to_id || null,
        assigned_to_name: assignee?.full_name || null,
        priority: form.priority,
        due_date: form.due_date || null,
        notes: form.notes,
      });
      if (data) setTasks(prev => prev.map(t => t.id === editingTask.id ? data : t));
    } else {
      const { data, error: taskErr } = await createTask({
        title: form.title,
        client_id: form.client_id || null,
        client_name: client ? `${client.first_name} ${client.last_name}` : null,
        client_ref: client?.client_id,
        assigned_to_id: form.assigned_to_id || null,
        assigned_to_name: assignee?.full_name || null,
        priority: form.priority,
        due_date: form.due_date || null,
        notes: form.notes,
        created_by_id: profile?.id || null,
        created_by_name: profile?.full_name || null,
        status: 'Open',
      });
      if (taskErr) {
        console.error('Task create error:', taskErr);
        setModalError(`Could not create task: ${taskErr.message}`);
        return;
      }
      if (data) {
        setTasks(prev => [data, ...prev]);
        if (form.assigned_to_id && form.assigned_to_id !== profile?.id && assignee?.email) {
          notifyTaskAssigned(data, assignee.full_name, assignee.email, profile.full_name);
        }
      }
    }
    setShowModal(false);
    setEditingTask(null);
  };

  const sorting = useMemo(() => sortBy ? [{ id: sortBy, desc: !sortAsc }] : [], [sortBy, sortAsc]);
  const handleSortingChange = (updaterOrValue) => {
    const next = typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;
    if (!next.length) return;
    setSortBy(next[0].id);
    setSortAsc(!next[0].desc);
  };

  const filtered = tasks
    .filter(t => filter === 'All' ? true : t.status === filter)
    .sort((a, b) => {
      let aVal = a[sortBy] || '';
      let bVal = b[sortBy] || '';
      if (sortBy === 'due_date') {
        aVal = aVal ? new Date(aVal).getTime() : Infinity;
        bVal = bVal ? new Date(bVal).getTime() : Infinity;
      }
      if (aVal < bVal) return sortAsc ? -1 : 1;
      if (aVal > bVal) return sortAsc ? 1 : -1;
      return 0;
    });

  const columns = [
    {
      id: 'done', header: '', size: 36, enableSorting: false,
      meta: { mobile: 'hidden' },
      cell: info => {
        const task = info.row.original;
        return (
          <div
            onClick={e => { e.stopPropagation(); handleStatusChange(task, task.status === 'Done' ? 'Open' : 'Done'); }}
            style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${task.status === 'Done' ? 'var(--success)' : 'var(--border)'}`, background: task.status === 'Done' ? 'var(--success)' : 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }}
          >
            {task.status === 'Done' && <svg width="10" height="10" fill="none" stroke="#fff" strokeWidth="3" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg>}
          </div>
        );
      },
    },
    {
      id: 'title', accessorKey: 'title', header: 'Task', size: 240,
      meta: { mobile: 'primary' },
      cell: info => {
        const task = info.row.original;
        return (
          <div>
            <div style={{ fontWeight: 500, color: 'var(--text)', textDecoration: task.status === 'Done' ? 'line-through' : '', maxWidth: 240 }}>{task.title}</div>
            {task.notes && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{task.notes.slice(0, 60)}{task.notes.length > 60 ? '...' : ''}</div>}
          </div>
        );
      },
    },
    {
      id: 'client_name', accessorKey: 'client_name', header: 'Client', size: 150,
      meta: { mobile: 'secondary' },
      cell: info => {
        const task = info.row.original;
        return task.client_name ? (
          <span className="td-link" onClick={e => { e.stopPropagation(); navigate(`/clients/${task.client_id}`); }}>{task.client_name}</span>
        ) : <span style={{ color: 'var(--text3)' }}>—</span>;
      },
    },
    {
      id: 'assigned_to_name', accessorKey: 'assigned_to_name', header: 'Assigned To', size: 140,
      meta: { mobile: 'secondary' },
      cell: info => <span style={{ color: 'var(--text2)' }}>{info.getValue() || '—'}</span>,
    },
    {
      id: 'due_date', accessorKey: 'due_date', header: 'Due Date', size: 110,
      meta: { mobile: 'primary' },
      cell: info => {
        const task = info.row.original;
        const pillInfo = duePill(task.due_date, task.status);
        return pillInfo ? (
          <span style={{ fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 20, background: pillInfo.bg, color: pillInfo.color }}>{pillInfo.label}</span>
        ) : task.due_date ? format(parseLocalDate(task.due_date), 'MMM d') : <span style={{ color: 'var(--text3)' }}>—</span>;
      },
    },
    {
      id: 'priority', accessorKey: 'priority', header: 'Priority', size: 100,
      meta: { mobile: 'secondary' },
      cell: info => <span className={`badge badge-${info.getValue()?.toLowerCase()}`}>{info.getValue()}</span>,
    },
    {
      id: 'status', header: 'Status', size: 140, enableSorting: false,
      meta: { mobile: 'secondary' },
      cell: info => {
        const task = info.row.original;
        return (
          <select
            value={task.status}
            onClick={e => e.stopPropagation()}
            onChange={e => handleStatusChange(task, e.target.value)}
            style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, border: '0.5px solid var(--border2)', fontFamily: 'inherit', cursor: 'pointer', background: task.status === 'Done' ? 'var(--success-bg)' : task.status === 'In Progress' ? 'var(--warn-bg)' : 'var(--bg2)', color: task.status === 'Done' ? 'var(--success)' : task.status === 'In Progress' ? 'var(--warn)' : 'var(--text2)' }}
          >
            <option value="Open">Open</option>
            <option value="In Progress">In Progress</option>
            <option value="Done">Done</option>
          </select>
        );
      },
    },
    {
      id: 'actions', header: 'Actions', size: 80, enableSorting: false,
      meta: { mobile: 'secondary' },
      cell: info => (
        <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={e => { e.stopPropagation(); openEdit(info.row.original); }}>Edit</button>
      ),
    },
  ];

  return (
    <div>
      <div className="grid-2">
        {/* Main Task Table */}
        <div className="card" style={{ gridColumn:'1 / -1' }}>
          <div className="card-header" style={{ background:'var(--accent-bg)', borderBottom:'1px solid var(--accent-border)' }}>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              {STATUSES.map(f => (
                <button key={f} onClick={() => setFilter(f)} style={{ padding:'4px 12px', borderRadius:20, fontSize:12, fontWeight:500, cursor:'pointer', fontFamily:'inherit', transition:'all 0.15s', border:'1px solid', background:filter===f?'var(--accent)':'transparent', color:filter===f?'#fff':'var(--text2)', borderColor:filter===f?'var(--accent)':'var(--border)' }}>{f}</button>
              ))}
            </div>
            <button className="btn btn-primary btn-sm" onClick={openCreate}>+ Add Task</button>
          </div>

          <DataTable
            tableId="tasks"
            columns={columns}
            data={filtered}
            getRowId={t => t.id}
            sorting={sorting}
            onSortingChange={handleSortingChange}
            manualSorting
            emptyMessage={`No ${filter !== 'All' ? filter.toLowerCase() : ''} tasks.`}
            maxHeight="calc(100vh - 320px)"
          />
        </div>

        {/* Team Workload */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">Team Workload</div>
          </div>
          <div style={{ padding:16 }}>
            {staff.map(member => {
              const openCount = tasks.filter(t => t.assigned_to_id === member.id && t.status !== 'Done').length;
              const inProgressCount = tasks.filter(t => t.assigned_to_id === member.id && t.status === 'In Progress').length;
              const max = Math.max(...staff.map(s => tasks.filter(t => t.assigned_to_id === s.id && t.status !== 'Done').length), 1);
              return (
                <div key={member.id} style={{ marginBottom:16 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:5 }}>
                    <span style={{ fontSize:13, fontWeight:500 }}>{fullNameLastFirst(member.full_name)}</span>
                    <div style={{ display:'flex', gap:8, fontSize:11 }}>
                      {inProgressCount > 0 && <span style={{ color:'var(--warn)' }}>{inProgressCount} in progress</span>}
                      <span style={{ color:'var(--text3)' }}>{openCount} open</span>
                    </div>
                  </div>
                  <div style={{ background:'var(--bg2)', borderRadius:4, height:8 }}>
                    <div style={{ background:'var(--accent)', height:8, borderRadius:4, width:`${(openCount/max)*100}%`, transition:'width 0.5s' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Summary stats */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">Summary</div>
          </div>
          <div style={{ padding:16 }}>
            {[
              { label:'Open', count:tasks.filter(t=>t.status==='Open').length, tone:'neutral' },
              { label:'In Progress', count:tasks.filter(t=>t.status==='In Progress').length, tone:'neutral' },
              { label:'Overdue', count:tasks.filter(t=>t.status!=='Done'&&t.due_date&&differenceInDays(parseLocalDate(t.due_date),new Date())<0).length, tone:'alert' },
              { label:'Done', count:tasks.filter(t=>t.status==='Done').length, tone:'neutral' },
            ].map(s => (
              <div key={s.label} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:'0.5px solid var(--border)' }}>
                <span style={{ fontSize:13, color:'var(--text2)' }}>{s.label}</span>
                <span style={{ fontSize:16, fontWeight:600, padding:'2px 12px', borderRadius:20, background:'var(--bg2)', color: s.tone === 'alert' && s.count > 0 ? 'var(--danger)' : 'var(--text)' }}>{s.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Create/Edit Task Modal */}
      {showModal && (
        <div className="modal-overlay" onMouseDown={e => e.target===e.currentTarget && setShowModal(false)}>
          <div className="modal" onMouseDown={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">{editingTask ? 'Edit Task' : 'New Task'}</div>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Task Title <span className="req">*</span></label>
                <input className="form-input" value={form.title} onChange={e => setForm(p=>({...p,title:e.target.value}))} placeholder="What needs to be done?" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Client</label>
                  <select className="form-select" value={form.client_id} onChange={e => setForm(p=>({...p,client_id:e.target.value}))}>
                    <option value="">No client</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name} ({c.client_id})</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Assign To</label>
                  <select className="form-select" value={form.assigned_to_id} onChange={e => setForm(p=>({...p,assigned_to_id:e.target.value}))}>
                    <option value="">Unassigned</option>
                    {staff.map(s => <option key={s.id} value={s.id}>{fullNameLastFirst(s.full_name)}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Priority</label>
                  <select className="form-select" value={form.priority} onChange={e => setForm(p=>({...p,priority:e.target.value}))}>
                    {PRIORITIES.map(p => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Due Date</label>
                  <input type="date" className="form-input" value={form.due_date} onChange={e => setForm(p=>({...p,due_date:e.target.value}))} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-textarea" rows={3} value={form.notes} onChange={e => setForm(p=>({...p,notes:e.target.value}))} placeholder="Additional details..." />
              </div>
              {editingTask && (
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="form-select" value={editingTask.status} onChange={e => handleStatusChange(editingTask, e.target.value)}>
                    <option value="Open">Open</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Done">Done</option>
                  </select>
                </div>
              )}
            </div>
            {modalError && (
              <div style={{ margin: '0 20px 12px', padding: '10px 14px', background: 'var(--danger-bg)', border: '1.5px solid var(--danger)', borderRadius: 8, fontSize: 13, color: 'var(--danger)' }}>
                ⚠ {modalError}
              </div>
            )}
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave}>{editingTask ? 'Save Changes' : 'Create Task'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
